import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { baseURL } from "aws-config";
import { palette } from "./palette";

const font = "Be Vietnam Pro";

// The lead form for visitors who aren't signed in.
//
// Deliberately two fields. It used to ask for a programme, name, email and
// phone — which asked more of a stranger than the signed-in flow asks of
// someone we already know, and sat under a heading promising "no form to
// fill". A name and a number is all anyone needs to call you back; the rest is
// what the first conversation is for.
const RegisterPopup = ({ open, onClose }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(true);
  const [errors, setErrors] = useState({ name: "", phone: "", consent: "" });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const validate = () => {
    const next = { name: "", phone: "", consent: "" };
    if (!name.trim()) next.name = "We'll need something to call you.";
    const digits = phone.replace(/[\s-]/g, "");
    if (!digits) next.phone = "We need a number to call you on.";
    else if (!/^\+?\d{10,15}$/.test(digits)) next.phone = "That doesn't look like a phone number.";
    if (!consent) next.consent = "We need your permission to get in touch.";
    setErrors(next);
    return !next.name && !next.phone && !next.consent;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      await axios.post(`${baseURL}request-call-details/`, {
        name: name.trim(),
        phone: phone.replace(/[\s-]/g, ""),
        program_name: "1:1 Tutoring",
      });
      setToast({
        open: true,
        message: "Got it — we'll call you within one working day.",
        severity: "success",
      });
      setName("");
      setPhone("");
      onClose();
    } catch (error) {
      setToast({
        open: true,
        message: "We couldn't send that just now. Try again in a moment.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    mb: 2.5,
    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
    "& .MuiInputBase-input": { fontFamily: font },
    "& .MuiInputLabel-root": { fontFamily: font },
    "& .MuiFormHelperText-root": { fontFamily: font },
  };

  return (
    <>
      <Modal open={open} onClose={onClose} aria-labelledby="talk-to-us-heading">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 440 },
            maxWidth: 440,
            bgcolor: palette.paper,
            color: palette.text,
            borderRadius: "20px",
            boxShadow: palette.shadowLg,
            p: { xs: 3, sm: 4 },
            outline: "none",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>
            <CloseIcon
              onClick={onClose}
              aria-label="Close"
              sx={{ cursor: "pointer", color: palette.textFaint, "&:hover": { color: palette.text } }}
            />
          </Box>

          <Typography
            component="h2"
            id="talk-to-us-heading"
            sx={{
              fontFamily: font,
              fontWeight: 700,
              fontSize: { xs: 24, sm: 27 },
              letterSpacing: "-0.025em",
              color: palette.text,
            }}
          >
            Let's talk
          </Typography>

          <Typography
            component="p"
            sx={{
              fontFamily: font,
              color: palette.textMuted,
              fontSize: 15.5,
              lineHeight: 1.65,
              mt: 1.5,
              mb: 3.5,
            }}
          >
            Leave us a number and one of us will call you — half an hour, no
            cost and no card, to work out where you are and what it would
            really take to get where you're going.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Your name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors((p) => ({ ...p, name: "" }));
              }}
              error={Boolean(errors.name)}
              helperText={errors.name}
              autoComplete="name"
              sx={fieldSx}
            />
            <TextField
              fullWidth
              label="Your phone number"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (errors.phone) setErrors((p) => ({ ...p, phone: "" }));
              }}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              autoComplete="tel"
              inputProps={{ maxLength: 16 }}
              sx={fieldSx}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    if (errors.consent) setErrors((p) => ({ ...p, consent: "" }));
                  }}
                  sx={{ color: palette.borderStrong, "&.Mui-checked": { color: palette.primary } }}
                />
              }
              label="I agree to be contacted on WhatsApp or SMS about this."
              sx={{
                alignItems: "flex-start",
                mb: 0.5,
                "& .MuiFormControlLabel-label": {
                  fontFamily: font,
                  fontSize: 14,
                  color: palette.textMuted,
                  mt: 1,
                },
              }}
            />
            {errors.consent && (
              <Typography sx={{ fontFamily: font, fontSize: 13, color: "#d32f2f", mb: 1 }}>
                {errors.consent}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={saving}
              disableElevation
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: "999px",
                background: palette.ctaGradient,
                color: "#ffffff",
                fontFamily: font,
                fontSize: 16,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { background: palette.primaryHover },
                "&.Mui-disabled": { background: palette.borderStrong, color: "#ffffff" },
              }}
            >
              {saving ? <CircularProgress size={22} color="inherit" /> : "Ask us to call"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} sx={{ fontFamily: font }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RegisterPopup;
